import { Dispatch, FC, SetStateAction, useMemo, useState } from 'react';

import { CustomPieChartTooltip } from './CustomPieChartTooltip';
import type { PieSectorShapeProps } from 'recharts';
import { Pie, PieChart as PieChartRecharts, Sector, Tooltip } from 'recharts';

import { Skeleton } from '@/components/atoms';
import { CHARTS_TEST_IDS } from '@/constants/data-testids';
import { cn } from '@/utils/tailwind';

import { formatNumberWithComma } from '../../../utils/numberFormatter';
import { Card, CardContent } from '../../atoms/Card/Card';
import { ChangeIndicator } from '../../atoms/ChangeIndicator/ChangeIndicator';

export type PieChartDataItem = {
  name: string;
  value: number;
  color: string;
  id: number;
};

type PieChartProps = {
  title: string;
  total: number;
  changeValue: number;
  data: PieChartDataItem[];
  isEditCellSkeleton?: boolean;
  size?: '1/3' | '2/3' | '3/3';
};
const MIN_ANGLE = 12;

const lightenColor = (hex: string, amount: number = 70): string => {
  const clamp = (val: number) => Math.min(255, Math.max(0, val));

  hex = hex.replace(/^#/, '');

  const num = Number.parseInt(hex, 16);
  const r = (num >> 16) & 0xff;
  const g = (num >> 8) & 0xff;
  const b = num & 0xff;

  const lighten = (channel: number) =>
    clamp(Math.round(channel + ((255 - channel) * amount) / 100));

  const newR = lighten(r);
  const newG = lighten(g);
  const newB = lighten(b);

  return (
    '#' +
    [newR, newG, newB]
      .map(x => x.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase()
  );
};

function createPieSectorShape(
  data: PieChartDataItem[],
  hoveredIndex: number | null,
  setHoveredIndex: Dispatch<SetStateAction<number | null>>
) {
  return function PieSectorShape(props: PieSectorShapeProps) {
    const { index } = props;
    const safeIndex = typeof index === 'number' ? index : 0;
    const entry = data[safeIndex];
    if (!entry) {
      return <Sector {...props} />;
    }
    const lightColor = lightenColor(entry.color, 30);
    return (
      <Sector
        {...props}
        fill={entry.color}
        stroke={hoveredIndex === safeIndex ? lightColor : 'transparent'}
        strokeWidth={4}
        onMouseEnter={() => setHoveredIndex(safeIndex)}
        onMouseLeave={() => setHoveredIndex(null)}
        className={cn(
          'transition-all duration-300 ease-in-out',
          hoveredIndex !== null && hoveredIndex !== safeIndex ? 'opacity-50' : 'opacity-100'
        )}
      />
    );
  };
}

export const PieChart: FC<PieChartProps> = ({
  data,
  title,
  total,
  changeValue,
  size = '3/3',
  isEditCellSkeleton,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const pieShape = useMemo(
    () => createPieSectorShape(data, hoveredIndex, setHoveredIndex),
    [data, hoveredIndex]
  );

  return (
    <Card
      data-testid={CHARTS_TEST_IDS.PIE_CHART}
      className="p-4 border-custom-warm-grey-150 rounded-[12px]"
    >
      <CardContent className="flex flex-col items-center justify-center">
        <div className="w-full mb-6">
          <h4 className="text-body-14-semibold !text-custom-warm-grey-700"> {title}</h4>
        </div>
        <div className="flex items-center my-8 mx-[50px]">
          <div className="relative">
            <PieChartRecharts className="relative z-10 shrink-0" width={220} height={220}>
              {!isEditCellSkeleton && <Tooltip content={<CustomPieChartTooltip />} />}
              <Pie
                data={data}
                dataKey="value"
                cx="50%"
                cy="50%"
                innerRadius={90}
                outerRadius={110}
                cornerRadius={10}
                paddingAngle={4}
                startAngle={90}
                endAngle={-270}
                minAngle={MIN_ANGLE}
                shape={pieShape}
              />
            </PieChartRecharts>
            <div className="absolute flex flex-col items-center justify-center top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              {isEditCellSkeleton ? (
                <div className="flex flex-col gap-[9px] items-center">
                  <Skeleton className="h-[20px] w-[71px]" />
                  <Skeleton className="h-[12px] w-[34px]" />
                </div>
              ) : (
                <>
                  <p className="text-body-28-bold !text-custom-warm-grey-800">
                    {formatNumberWithComma(total)}
                  </p>
                  <ChangeIndicator changeValue={changeValue} />
                </>
              )}
            </div>
          </div>

          <ul
            className={cn('flex flex-col gap-3 w-[200px] ml-10', {
              hidden: size === '1/3',
            })}
          >
            {data.map(entry => (
              <li key={entry.id} className="flex items-center gap-2">
                <svg className="shrink-0" width={12} height={12}>
                  <circle cx="6" cy="6" r="6" fill={entry.color} />
                </svg>
                {isEditCellSkeleton ? (
                  <Skeleton className="h-[12px] w-full" />
                ) : (
                  <>
                    <p className="text-body-12-medium !text-custom-warm-grey-800 grow">
                      {entry.name}
                    </p>
                    <p className="text-body-12-medium !text-custom-warm-grey-800">{entry.value}</p>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
